import { StaticFilterValues } from '../constants/StaticFilterValues';
import { filterEndpoints } from '../config/ProductLineFilterConfig';
import { fetchFilterData } from '../api/filters/FilterApi';

/**
 * Hook for managing ProductLine static filter data loading
 * Handles both node expansion and search functionality for ProductLine dropdowns
 * 
 * @param handleAsyncError - Error handler function for async operations
 * @returns Object containing loadFilterData function
 */
export const useProductLineStaticFilter = (
  handleAsyncError: (error: Error) => void
) => {
  /**
   * Loads filter data for a specific node or search term
   * Handles hierarchy mapping and parameter construction for API calls
   * 
   * @param filterType - Type of filter (e.g., 'productid')
   * @param node - Node object containing value, key, and level information
   * @param productHierarchy - Optional hierarchy type override
   * @param signal - Optional AbortSignal for request cancellation
   * @returns Promise resolving to filter data with children
   */
  const loadFilterData = async (filterType: string, node: any, productHierarchy?: string, signal?: AbortSignal) => {
    const config = filterEndpoints[filterType];
    if (!config?.isAsync) return;

    let hierarchyName = productHierarchy;
    
    if (filterType === StaticFilterValues.PRODUCT_ID && ['A1PTOT', 'PTOT', 'altProduct'].includes(node.value)) {
      const hierarchyMap: Record<string, string> = {
        'A1PTOT': 'COGNOS_PRODUCT_ALTERNATE_HIERARCHY',
        'PTOT': 'COGNOS_PRODUCT_HIERARCHY',
        'altProduct': 'COGNOS_PRODUCT_ALTERNATE_ALT_HIERARCHY'
      };
      hierarchyName = hierarchyMap[node.value];
    }

    const isSearchRequest = node && typeof node.key === 'string' && !node.value;
    
    const params: any = {
      hierarchyName: hierarchyName,
      filterValue: isSearchRequest ? node.key : (node.value ? node.value.split("_").pop() : ''),
    };
    
    if (node.level !== undefined) {
      params.level = node.level;
    } else if (!isSearchRequest) {
      params.level = node.pos ? node.pos.split('-').length - 1 : 0;
    }

    const data = await fetchFilterData(filterType, params, handleAsyncError, signal);
    return data;
  };

  return {
    loadFilterData
  };
};
